/**
 * Client document folders. Every client has the same folders in every year, grouped by service,
 * plus Permanent folders for papers that don't belong to a year (IDs, formation papers, records of
 * what an asset cost). Folders are fixed names, so they exist before anything is uploaded.
 *
 * Years: a tax document is filed under the year its return is filed (tax year + 1), so the 2026
 * W-2 that arrives in February 2027 sits in 2027, next to the rest of that season's work. Other
 * documents are filed under the year they cover. Every year stays open; next year's folder opens
 * for uploads on December 1.
 */

export type FolderGroupKey = "permanent" | "tax" | "accounting" | "payroll" | "advisory" | "general";

export const FOLDER_GROUPS: ReadonlyArray<{ key: FolderGroupKey; label: string; folders: readonly string[] }> = [
  { key: "permanent", label: "Permanent", folders: ["Identity Documents", "Business Formation", "Asset Records"] },
  { key: "tax", label: "Tax", folders: ["Tax Documents", "Tax Returns", "Tax Notices"] },
  { key: "accounting", label: "Accounting", folders: ["Bank Statements", "Receipts", "Bills", "Financial Statements", "Adjusting Entries", "Workpapers", "W-9s"] },
  { key: "payroll", label: "Payroll", folders: ["Payroll"] },
  { key: "advisory", label: "CFO & Advisory", folders: ["CFO Reports"] },
  { key: "general", label: "General", folders: ["Engagement Letters", "Consents", "Correspondence", "General"] },
];

export const STANDARD_FOLDERS: readonly string[] = FOLDER_GROUPS.flatMap((g) => g.folders);
export const PERMANENT_FOLDERS: readonly string[] = FOLDER_GROUPS.find((g) => g.key === "permanent")!.folders;
export const TAX_FOLDERS: readonly string[] = FOLDER_GROUPS.find((g) => g.key === "tax")!.folders;
export const PAYROLL_FOLDERS: readonly string[] = FOLDER_GROUPS.find((g) => g.key === "payroll")!.folders;

/** What each folder holds, in the client's words. */
export const FOLDER_HELP: Record<string, string> = {
  "Identity Documents": "Driver's licenses, passports, Social Security cards, IRS identity PIN letters",
  "Business Formation": "Articles, operating agreement, EIN letter, S corporation election",
  "Asset Records": "Closing statements, purchase records and improvements for homes, rentals and equipment",
  "Tax Documents": "W-2s, 1099s, 1098s, K-1s, receipts and anything else for the return",
  "Tax Returns": "Filed returns and signature forms",
  "Tax Notices": "Letters from the IRS or a state",
  "Bank Statements": "Bank, credit card and loan statements",
  Receipts: "Receipts and proof of business expenses",
  Bills: "Bills from vendors",
  "Financial Statements": "Profit and loss, balance sheet and other reports",
  "Adjusting Entries": "Support for adjusting entries",
  Workpapers: "Reconciliations and working papers",
  "W-9s": "Forms W-9 from vendors and contractors",
  Payroll: "Payroll reports, filings and employee forms",
  "CFO Reports": "Monthly CFO reports",
  "Engagement Letters": "Engagement letters and agreements",
  Consents: "Signed consents",
  Correspondence: "Letters and notes",
  General: "Anything else",
};

/** The folders a client may choose when uploading in the portal. */
export const CLIENT_UPLOAD_FOLDERS: readonly string[] = [
  "Tax Documents",
  "Tax Notices",
  "Bank Statements",
  "Receipts",
  "Bills",
  "Payroll",
  "Identity Documents",
  "Business Formation",
  "Asset Records",
  "General",
];

/** Older and informal names, mapped to the standard folder. */
const ALIASES: Record<string, string> = {
  "tax notices": "Tax Notices",
  notices: "Tax Notices",
  "irs letters": "Tax Notices",
  returns: "Tax Returns",
  "tax returns": "Tax Returns",
  tax: "Tax Documents",
  "source documents": "Tax Documents",
  "tax documents": "Tax Documents",
  statements: "Financial Statements",
  "bank statements": "Bank Statements",
  engagement: "Engagement Letters",
  "engagement letters": "Engagement Letters",
  uploads: "General",
};

/**
 * The standard name for a folder: matches regardless of case, maps old names ("Tax notices",
 * "2026 Returns") and keeps any other name as typed (staff may add their own folders).
 */
export function normalizeFolder(name: string): string {
  const t = name.trim().replace(/\s+/g, " ");
  const bare = t.replace(/^(19|20)\d\d\s+/, "");
  const lower = bare.toLowerCase();
  const standard = STANDARD_FOLDERS.find((f) => f.toLowerCase() === lower);
  return standard ?? ALIASES[lower] ?? (t || "General");
}

export function folderGroup(folder: string): FolderGroupKey | "other" {
  return FOLDER_GROUPS.find((g) => g.folders.includes(folder))?.key ?? "other";
}

export const isPermanentFolder = (folder: string) => PERMANENT_FOLDERS.includes(folder);

/** The latest year open for uploads on a given day: next year's folder opens on December 1. */
export function latestOpenYear(today: string): number {
  const y = Number(today.slice(0, 4));
  return today.slice(5, 7) === "12" ? y + 1 : y;
}

/** Years shown in a year picker: the open years back to `earliest`, newest first. */
export function yearChoices(today: string, earliest: number): number[] {
  const out: number[] = [];
  for (let y = latestOpenYear(today); y >= earliest; y--) out.push(y);
  return out;
}

// --- Retention ---------------------------------------------------------------------------------

export type RetentionPolicy = { years: number; payrollYears: number };
export const DEFAULT_RETENTION: RetentionPolicy = { years: 3, payrollYears: 4 };
/** The shortest periods a firm may set: the IRS can look back 3 years (4 for payroll tax records). */
export const MIN_RETENTION: RetentionPolicy = { years: 3, payrollYears: 4 };

/**
 * The newest folder year whose documents may be deleted in `currentYear`. Tax folders are filing
 * years, so the 2027 folder (filed by April 2027) is kept through 2030 and may go from 2031. Other
 * folders are the year the records cover, which is filed the year after, so they wait a year more.
 */
export function retentionCutoffs(currentYear: number, p: RetentionPolicy): { tax: number; other: number; payroll: number } {
  return { tax: currentYear - p.years - 1, other: currentYear - p.years - 2, payroll: currentYear - p.payrollYears - 2 };
}

/** How many days clients are given to download documents before they are deleted. */
export const RETENTION_NOTICE_DAYS = 60;
